/* eslint-disable ember/no-runloop */
import { isArray } from '@ember/array';
import { later } from '@ember/runloop';
import { cleanExcelSheetName } from '@linc-technologies/linced-common/utils/file-names';
import { isEmpty } from '@ember/utils';

export function getTablePrintCss(landscape: boolean): string {
  return `
    <link rel="stylesheet" href="${
      window.location.origin
    }/assets/icons/linced-material.css">
    <style type="text/css">
      /* Don't print occluded content placeholder */
      occluded-content {
        display: none;
      }

      table {
        width: 100%;
      }
      table, tr, td, th {
        border: 1px solid black;
        border-collapse: collapse;
      }
      tr {
        page-break-inside: avoid;
        page-break-after:auto
      }
      td, th {
        padding: 2px;
        word-wrap: break-word;
      }
      .hide-print,
      tr.field-filters,
      thead md-icon.md-sort-icon {
        display: none;
      }
      table ul {
        margin-bottom: 0;
      }
      .dob-labels td {
        height: 150px;
        line-height: 2;
        padding: 0 20px;
      }
      .attendance-audit table {
        font-size: 50%;
      }

      .md-button {
        white-space: nowrap;
        text-transform: uppercase;
      }

      md-progress-linear {
        display: block;
        position: relative;
        width: 100%;
        height: 5px;
        padding-top: 0 !important;
        margin-bottom: 0 !important;
      }
      md-progress-linear._md-progress-linear-disabled {
        visibility: hidden;
      }
      md-progress-linear .md-container {
        display: block;
        position: relative;
        overflow: hidden;
        width: 100%;
        height: 5px;
      }
      md-progress-linear .md-container .md-bar {
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 100%;
        height: 5px;
      }
      md-progress-linear .md-container .md-dashed:before {
        content: "";
        display: none;
        position: absolute;
        margin-top: 0;
        height: 5px;
        width: 100%;
        background-color: transparent;
        background-size: 10px 10px !important;
        background-position: 0px -23px;
      }
      md-progress-linear .md-container.md-mode-buffer {
        background-color: transparent !important;
      }
      md-progress-linear .md-container.md-mode-buffer .md-dashed:before {
        display: block;
      }

      .page-break-after,
      .page-break-after > table {
        break-after: page;
      }

      .auto-page-break-after,
      .auto-page-break-after > table {
        break-after: auto;
        break-inside: avoid;
      }

      ${landscape ? `@media print{@page {size: landscape}}` : ``}
    </style>
  `;
}

export const tablePrintCss = getTablePrintCss(false);

// DYNAMIC_SELECTOR matches the selectors of the linced-dynamic-styles rules.
const DYNAMIC_SELECTOR = /progress-colour-|colours-progress--|circle--/;

// getDynamicStyles gives a style element with the school colours, for a print
// document that does not load the app stylesheets. It copies the
// linced-dynamic-styles rules: the default gender colours on :root, and the
// rules that read the progress colours. Then it adds the school colours of
// DynamicStyles, which replace the defaults.
export function getDynamicStyles(): string {
  let css = '';
  for (const sheet of document.styleSheets) {
    let rules;
    try {
      rules = sheet.cssRules;
    } catch {
      // A stylesheet from another origin does not give its rules.
      continue;
    }
    for (const rule of rules) {
      if (!(rule instanceof CSSStyleRule)) {
        continue;
      }
      const text = rule.style.cssText;
      if (
        (rule.selectorText === ':root' && text.includes('--gender-color-')) ||
        (DYNAMIC_SELECTOR.test(rule.selectorText) &&
          text.includes('var(--progress-colour-'))
      ) {
        css += rule.cssText;
      }
    }
  }
  css += document.querySelector('style.dynamic-styles')?.textContent ?? '';
  return `<style type="text/css">${css}</style>`;
}

// A merged cell range, as SheetJS takes it: start and end, by row and column.
export interface Merge {
  s: { r: number; c: number };
  e: { r: number; c: number };
}

export interface ExportData<T> {
  rows: T[][];
  merges: Merge[];
}

export function getTableExportData<T>(
  containerId: string,
  format: string,
  parseCell: (cell: HTMLElement) => T,
  bodyData: T[][] | null = null,
): ExportData<T> {
  const notCheckbox = ':not(.md-checkbox-column):not(.skip-export)';
  let table = `#${containerId} > table > div`;
  // Check if div exists or not
  if (isEmpty(document.querySelectorAll(table))) {
    table = `#${containerId} > table`;
  }
  const headerSelector = `${table} > thead > tr:not(.skip-export)`;
  const headerRows = document.querySelectorAll(headerSelector);
  const header = getExportData(
    headerRows,
    `${headerSelector} > th${notCheckbox}`,
    format,
    parseCell,
  );
  const bodySelector = `${table} > tbody > tr:not(.field-filters)`;
  const bodyRows = document.querySelectorAll(bodySelector);
  const body: ExportData<T> = isArray(bodyData)
    ? { rows: bodyData, merges: [] }
    : getExportData(
        bodyRows,
        `${bodySelector} > td${notCheckbox}`,
        format,
        parseCell,
      );
  const footerSelector = `${table} > tfoot > tr`;
  const footerRows = document.querySelectorAll(footerSelector);
  const footerCells = `${footerSelector} > th${notCheckbox}, ${footerSelector} > td${notCheckbox}`;
  const footer = getExportData(footerRows, footerCells, format, parseCell);

  // Build a single array of all rows (header + body + footer).
  const data: T[][] = [];
  data.push(...header.rows);
  data.push(...body.rows);
  data.push(...footer.rows);

  // Build a single array of all merges, offsetting body and footer by the relevant number of rows.
  const offsetMerges = (merge: Merge) => {
    merge.s.r += offset;
    merge.e.r += offset;
  };
  const merges: Merge[] = [];
  merges.push(...header.merges);
  let offset = header.rows.length;
  body.merges.forEach(offsetMerges);
  merges.push(...body.merges);
  offset = header.rows.length + body.rows.length;
  footer.merges.forEach(offsetMerges);
  merges.push(...footer.merges);
  return { rows: data, merges };
}

function getExportData<T>(
  rows: NodeListOf<Element>,
  selector: string,
  format: string,
  parseCell: (cell: HTMLElement) => T,
): ExportData<T> {
  const output: T[][] = [];
  const merges: Merge[] = [];

  // Add a row to the output array for each row in the table's header.
  rows.forEach(() => output.push([]));

  let r = 0;
  rows.forEach((row) => {
    let c = 0;
    // Every row has an output row, added above.
    const outputRow = output[r] as T[];
    row.querySelectorAll<HTMLElement>(selector).forEach((cell) => {
      const rowSpan = Number(cell.getAttribute('rowspan'));
      const colSpan = Number(cell.getAttribute('colspan')) || 1;
      const cellValue = parseCell(cell);

      // Apply merge offsets if necessary
      for (let midx = 0; midx < merges.length; ++midx) {
        const m = merges[midx] as Merge;
        if (m.s.c === c && m.s.r <= r && r <= m.e.r) {
          c = m.e.c + 1;
          midx = -1;
        }
      }

      if (rowSpan > 0 || colSpan > 1) {
        merges.push({
          s: { r, c },
          e: { r: r + (rowSpan || 1) - 1, c: c + colSpan - 1 },
        });

        if (format === 'xlsx') {
          outputRow[c] = cellValue;
        } else {
          // CSV - populate all the merged cells with the same value.
          // A rowspan can go past the last row. Stop at the last row.
          const lastRow = Math.min(r + (rowSpan || 1), output.length);
          for (let outRow = r; outRow < lastRow; outRow++) {
            for (let outCol = c; outCol < c + colSpan; outCol++) {
              (output[outRow] as T[])[outCol] = cellValue;
            }
          }
        }
      } else {
        // No spans, just a single cell.
        outputRow[c] = cellValue;
      }
      c += colSpan;
    });
    r++;
  });

  return { rows: output, merges };
}

// parseCell is a default implementation of parsing a table cell to generate plain text for spreadsheet exports.
export function parseCell(cell: HTMLElement): string {
  // Deep clone the cell, so we don't change the actual cell.
  const clone = cell.cloneNode(true) as HTMLElement;

  // Each matched element is a descendant of the clone, so always has a parent.
  // Replace all title-bearing elements with their titles.
  clone.querySelectorAll<HTMLElement>('[title]').forEach((el) => {
    const textNode = document.createTextNode(el.title);
    (el.parentNode as Node).replaceChild(textNode, el);
  });
  // And any which use data-title to avoid browsers rendering the title as a tooltip.
  clone.querySelectorAll<HTMLElement>('[data-title]').forEach((el) => {
    const textNode = document.createTextNode(el.dataset.title as string);
    (el.parentNode as Node).replaceChild(textNode, el);
  });

  // Remove sort direction icons
  clone
    .querySelectorAll('.md-sort-icon')
    .forEach((el) => (el.parentNode as Node).removeChild(el));

  // Return the element's innerText, with all excess whitespace removed.
  return clone.innerText.replace(/\s+/g, ' ').trim();
}

export function getMultiTablesExportData(
  titles: ArrayLike<HTMLElement>,
  tables: ArrayLike<HTMLElement>,
): { name: string; data: string[][] }[] {
  const sheets: { name: string; data: string[][] }[] = [];

  // Each title is paired with the table at the same index.
  for (let i = 0; i < titles.length; i++) {
    const sheetName = cleanExcelSheetName((titles[i] as HTMLElement).innerText);
    const data = getTableExportData(
      (tables[i] as HTMLElement).id,
      'xlsx',
      parseCell,
    );

    sheets.push({
      name: sheetName,
      data: data.rows,
    });
  }

  return sheets;
}

// printMultiTables returns false when a popup blocker stops the print window.
export function printMultiTables(
  this: unknown,
  heading: string,
  titles: ArrayLike<HTMLElement>,
  tables: ArrayLike<HTMLElement>,
): boolean {
  const printer = window.open(
    'about:blank',
    '_blank',
    'menubar=yes,location=no,resizable=yes,scrollbars=yes,status=no,height=700,width=1024',
  );
  if (!printer) {
    return false;
  }

  const title = `<h1>${heading}</h1>`;
  const styles = getTablePrintCss(true) + getDynamicStyles();

  let content = '';

  for (let i = 0; i < titles.length; i++) {
    content += `<div class="auto-page-break-after">
                      ${(titles[i] as HTMLElement).outerHTML}
                      ${(tables[i] as HTMLElement).outerHTML}
                    </div>`;
  }

  printer.document.body.innerHTML = styles + title + content;

  later(
    this,
    (printer: Window) => {
      printer.print();
      printer.close();
    },
    printer,
    500,
  );

  return true;
}
